import { useEffect, useMemo, useRef, useState } from 'react';
import {
  toEngineHandicap,
  type ClockState,
  type Frame,
  type HandicapConfig,
  type CapturedPiece,
  type MatchEndedPayload,
  type MatchMovePayload,
  type MatchResultBody,
  type MatchSnapshotPayload,
  type MatchStartedPayload,
  type MatchChatPayload,
  type MatchSpectatorsPayload,
  type MatchReplayData,
  type MoveRejectCode,
  type PlayerRef,
  type ResultReason,
  type Ruleset,
  type Side,
} from '@xiangqi/protocol';
import type { GameStateJson, PieceKindId, XiangqiCore } from '@xiangqi/game-core';
import { MatchSocket, type ConnectionStatus } from '../net/socket.ts';
import { getCore } from '../wasm.ts';
import { sfx } from '../audio/sfx.ts';
import { api, currentUser } from '../api.ts';
import {
  generateSimulatedPlayer,
  getSimulatedThinkingDelay,
  simulatedToPlayerRef,
  type SimulatedPlayerProfile,
} from './fake-player.ts';

export interface MatchView {
  loaded: boolean;
  conn: ConnectionStatus;
  status: 'waiting' | 'running' | 'finished';
  state: GameStateJson | null;
  yourSide: Side | null;
  sides: { red: PlayerRef; black: PlayerRef } | null;
  clocks: ClockState | null;
  /** serverTime - localTime, for clock interpolation. */
  serverOffset: number;
  ply: number;
  lastMove: { from: number; to: number } | null;
  pendingMove: { from: number; to: number } | null;
  premove: { from: number; to: number } | null;
  result: MatchResultBody | null;
  drawOfferBy: Side | null;
  rematchOfferBy: Side | null;
  rematchStartedNextMatchId: string | null;
  rejection: { text: string; at: number } | null;
  handicap: HandicapConfig | null;
  capturedPieces: CapturedPiece[];
  spectatorCount: number;
  chatMessages: MatchChatPayload[];
}

const REJECT_TEXT: Record<MoveRejectCode, string> = {
  NOT_YOUR_TURN: 'Chưa đến lượt bạn',
  ILLEGAL_MOVE: 'Nước đi không hợp lệ',
  PLY_CONFLICT: 'Bàn cờ đã thay đổi — thử lại',
  GAME_OVER: 'Ván đấu đã kết thúc',
  NOT_IN_MATCH: 'Bạn không thuộc ván này',
  MATCH_NOT_RUNNING: 'Ván chưa bắt đầu',
};

const INITIAL: MatchView = {
  loaded: false,
  conn: 'connecting',
  status: 'waiting',
  state: null,
  yourSide: null,
  sides: null,
  clocks: null,
  serverOffset: 0,
  ply: 0,
  lastMove: null,
  pendingMove: null,
  premove: null,
  result: null,
  drawOfferBy: null,
  rematchOfferBy: null,
  rematchStartedNextMatchId: null,
  rejection: null,
  handicap: null,
  capturedPieces: [],
  spectatorCount: 0,
  chatMessages: [],
};

export interface MatchActions {
  move(from: number, to: number): void;
  cancelPremove(): void;
  resign(): void;
  offerDraw(): void;
  respondDraw(accept: boolean): void;
  offerRematch(): void;
  respondRematch(accept: boolean): void;
  sendChat(text: string): void;
}

export function useMatch(matchId: string): { view: MatchView; actions: MatchActions; core: XiangqiCore | null } {
  const isLocal = matchId.startsWith('local-');
  const [view, setView] = useState<MatchView>(INITIAL);
  const [core, setCore] = useState<XiangqiCore | null>(null);
  const socketRef = useRef<MatchSocket | null>(null);
  const coreRef = useRef<XiangqiCore | null>(null);
  const viewRef = useRef<MatchView>(view);
  const sentPremoveRef = useRef<string | null>(null);
  const localMovesRef = useRef<Array<{ ply: number; from: number; to: number; notation: string; ms_spent: number }>>([]);
  const localSeedRef = useRef<string>(Date.now().toString());
  const localStartedAtRef = useRef<string>(new Date().toISOString());
  const simPlayerRef = useRef<SimulatedPlayerProfile | null>(null);
  if (isLocal && !simPlayerRef.current) {
    simPlayerRef.current = generateSimulatedPlayer(matchId);
  }
  viewRef.current = view;

  const computeNextClocks = (
    currentClocks: ClockState | null,
    moverSide: Side,
    nextTurn: Side,
    isOver: boolean,
    incrementMs = 5000,
  ): ClockState => {
    const fallback: ClockState = {
      red_ms: 600_000,
      black_ms: 600_000,
      turn: nextTurn,
      deadline_at: null,
    };
    const c = currentClocks ?? fallback;
    const now = Date.now();
    const remaining = c.deadline_at !== null
      ? Math.max(0, c.deadline_at - now)
      : (moverSide === 'red' ? c.red_ms : c.black_ms);
    const updatedMoverMs = remaining + (incrementMs > 0 ? incrementMs : 0);
    const red_ms = moverSide === 'red' ? updatedMoverMs : c.red_ms;
    const black_ms = moverSide === 'black' ? updatedMoverMs : c.black_ms;
    const nextTurnMs = nextTurn === 'red' ? red_ms : black_ms;
    return {
      red_ms,
      black_ms,
      turn: nextTurn,
      deadline_at: isOver ? null : now + nextTurnMs,
    };
  };

  const persistLocalMatch = (
    nextState: GameStateJson,
    status: 'waiting' | 'running' | 'finished',
    result: MatchResultBody | null,
    ruleset: Ruleset,
    handicap: HandicapConfig | null,
    userSide: Side,
    botDiff: string,
    clocks?: ClockState,
  ) => {
    try {
      const matchData = {
        state: nextState,
        status,
        result,
        ruleset,
        seed: localSeedRef.current,
        handicap,
        moves: localMovesRef.current,
        userSide,
        botDiff,
        simPlayer: simPlayerRef.current,
        started_at: localStartedAtRef.current,
        clocks,
      };
      sessionStorage.setItem(`xq_local_match_${matchId}`, JSON.stringify(matchData));
      if (status === 'finished') {
        const playerName = currentUser()?.display_name || 'Bạn';
        const opponentName = simPlayerRef.current?.displayName || 'Đối thủ';
        const replayData: MatchReplayData = {
          match_id: matchId,
          ruleset,
          seed: localSeedRef.current,
          ...(handicap ? { handicap } : {}),
          players: {
            red: {
              user_id: userSide === 'red' ? 'player' : (simPlayerRef.current?.userId ?? 'opp'),
              display_name: userSide === 'red' ? playerName : opponentName,
            },
            black: {
              user_id: userSide === 'black' ? 'player' : (simPlayerRef.current?.userId ?? 'opp'),
              display_name: userSide === 'black' ? playerName : opponentName,
            },
          },
          result,
          moves: localMovesRef.current,
          started_at: localStartedAtRef.current,
          ended_at: new Date().toISOString(),
        };
        sessionStorage.setItem(`xq_replay_${matchId}`, JSON.stringify(replayData));
      }
    } catch {
      // ignore storage error
    }
  };

  useEffect(() => {
    if (
      !core ||
      !view.state ||
      !view.premove ||
      view.pendingMove ||
      view.status !== 'running' ||
      view.result ||
      !view.yourSide ||
      view.state.turn !== view.yourSide ||
      (!isLocal && !socketRef.current)
    ) {
      return;
    }
    const { from, to } = view.premove;
    const key = `${view.ply}:${from}:${to}`;
    if (sentPremoveRef.current === key) return;
    let legal = false;
    try {
      legal = core.legalMoves(view.state).moves.some((move) => move.from === from && move.to === to);
    } catch {
      /* stale premove: discard it */
    }
    if (!legal) {
      sentPremoveRef.current = null;
      setView((v) => (v.premove === view.premove ? { ...v, premove: null } : v));
      return;
    }
    sentPremoveRef.current = key;
    if (isLocal) {
      try {
        const { state, outcome } = core.applyMove(view.state, { from, to });
        if (outcome.captured) sfx.playCapture();
        else sfx.playMove();
        if (outcome.check) sfx.playCheck();

        const isOver = !!outcome.result;
        if (outcome.result) {
          if (outcome.result.winner === view.yourSide) sfx.playVictory();
          else if (outcome.result.winner !== null) sfx.playDefeat();
        }

        localMovesRef.current.push({
          ply: view.ply,
          from,
          to,
          notation: outcome.notation ?? '',
          ms_spent: 200,
        });

        const nextClocks = computeNextClocks(
          view.clocks,
          view.state.turn,
          state.turn,
          isOver,
        );

        let botDiff = 'medium';
        try {
          const raw = localStorage.getItem('xq_saved_config');
          if (raw) botDiff = JSON.parse(raw).bot_difficulty || JSON.parse(raw).difficulty || 'medium';
        } catch {
          // ignore
        }

        persistLocalMatch(
          state,
          isOver ? 'finished' : 'running',
          outcome.result ?? null,
          view.state.ruleset,
          view.handicap,
          view.yourSide ?? 'red',
          botDiff,
          nextClocks,
        );

        setView((v) => ({
          ...v,
          state,
          ply: v.ply + 1,
          lastMove: { from, to },
          pendingMove: null,
          premove: null,
          capturedPieces: (state.captured_pieces as CapturedPiece[]) ?? v.capturedPieces,
          result: outcome.result ?? null,
          status: isOver ? 'finished' : 'running',
          clocks: nextClocks,
        }));

        if (!isOver && state.turn !== view.yourSide) {
          scheduleBotMove(400);
        }
      } catch {
        sentPremoveRef.current = null;
        setView((v) => ({ ...v, premove: null }));
      }
      return;
    }
    const socket = socketRef.current;
    if (!socket) return;
    socket.sendMove({ command_id: crypto.randomUUID(), ply: view.ply, move: { from, to } });
    setView((v) =>
      v.premove === view.premove
        ? { ...v, premove: null, pendingMove: { from, to }, rejection: null }
        : v,
    );
  }, [view, core, isLocal]);

  useEffect(() => {
    let cancelled = false;

    if (isLocal) {
      void getCore().then((c) => {
        if (cancelled) return;
        coreRef.current = c;
        setCore(c);

        let ruleset: Ruleset = 'standard';
        let handicap: HandicapConfig | undefined = undefined;
        let userSide: Side = 'red';
        let botDiff = 'medium';

        try {
          const raw = localStorage.getItem('xq_saved_config');
          if (raw) {
            const saved = JSON.parse(raw);
            if (saved.ruleset) ruleset = saved.ruleset;
            if (saved.handicap && saved.handicap.type !== 'none') {
              const hType = saved.handicap.type;
              if (
                (hType === 'asym_royal' && (ruleset === 'blind_si_chua' || ruleset === 'blind_tuong_vuong')) ||
                ((hType === 'piece' || hType === 'moves') && ruleset === 'standard') ||
                (hType === 'none' && saved.handicap.time)
              ) {
                handicap = saved.handicap;
              }
            }
            if (saved.side || saved.playerSide) userSide = saved.side || saved.playerSide;
            if (saved.bot_difficulty || saved.difficulty) botDiff = saved.bot_difficulty || saved.difficulty;
          }
        } catch {
          // ignore
        }

        if (ruleset === 'blind') {
          handicap = undefined;
        }

        const savedSession = sessionStorage.getItem(`xq_local_match_${matchId}`);
        if (savedSession) {
          try {
            const parsed = JSON.parse(savedSession);
            localSeedRef.current = parsed.seed || localSeedRef.current;
            localStartedAtRef.current = parsed.started_at || localStartedAtRef.current;
            localMovesRef.current = parsed.moves || [];
            userSide = parsed.userSide || userSide;
            botDiff = parsed.botDiff || botDiff;

            if (parsed.simPlayer) {
              simPlayerRef.current = parsed.simPlayer;
            }
            const playerName = currentUser()?.display_name || 'Bạn';
            const opponentRef = simulatedToPlayerRef(simPlayerRef.current!);

            const savedClocks = parsed.clocks as ClockState | undefined;
            const restoredTurn = (parsed.state?.turn as Side) || userSide;
            const redMs = savedClocks?.red_ms ?? 600_000;
            const blackMs = savedClocks?.black_ms ?? 600_000;
            const activeMs = restoredTurn === 'red' ? redMs : blackMs;
            const restoredClocks: ClockState = {
              red_ms: redMs,
              black_ms: blackMs,
              turn: restoredTurn,
              deadline_at: parsed.status === 'running' ? Date.now() + activeMs : null,
            };

            const restoredView: MatchView = {
              loaded: true,
              conn: 'live',
              status: parsed.status,
              state: parsed.state,
              yourSide: userSide,
              sides: {
                red: userSide === 'red' ? { user_id: 'player', display_name: playerName, connected: true } : opponentRef,
                black: userSide === 'black' ? { user_id: 'player', display_name: playerName, connected: true } : opponentRef,
              },
              clocks: restoredClocks,
              serverOffset: 0,
              ply: parsed.moves?.length ?? 0,
              lastMove: parsed.moves?.length ? parsed.moves[parsed.moves.length - 1] : null,
              pendingMove: null,
              premove: null,
              result: parsed.result ?? null,
              drawOfferBy: null,
              rematchOfferBy: null,
              rematchStartedNextMatchId: null,
              rejection: null,
              handicap: parsed.state?.ruleset === 'blind' ? null : (parsed.handicap ?? null),
              capturedPieces: (parsed.state.captured_pieces as CapturedPiece[]) ?? [],
              spectatorCount: 0,
              chatMessages: [],
            };

            viewRef.current = restoredView;
            setView(restoredView);

            if (parsed.status === 'running' && parsed.state.turn !== userSide) {
              scheduleBotMove(getSimulatedThinkingDelay(parsed.moves?.length ?? 0));
            }
            return;
          } catch {
            // fallback to new game
          }
        }

        const engineHandicap = toEngineHandicap(ruleset, handicap);
        const st = c.newGame(
          ruleset,
          BigInt(localSeedRef.current || '0'),
          engineHandicap,
        );

        const now = Date.now();
        const initialTurn = st.turn as Side;
        const initialMs = 600_000;
        const initialClocks: ClockState = {
          red_ms: initialMs,
          black_ms: initialMs,
          turn: initialTurn,
          deadline_at: now + initialMs,
        };

        persistLocalMatch(
          st,
          'running',
          null,
          ruleset,
          handicap ?? null,
          userSide,
          botDiff,
          initialClocks,
        );

        const playerName = currentUser()?.display_name || 'Bạn';
        const opponentRef = simulatedToPlayerRef(simPlayerRef.current!);

        const newView: MatchView = {
          loaded: true,
          conn: 'live',
          status: 'running',
          state: st,
          yourSide: userSide,
          sides: {
            red: userSide === 'red' ? { user_id: 'player', display_name: playerName, connected: true } : opponentRef,
            black: userSide === 'black' ? { user_id: 'player', display_name: playerName, connected: true } : opponentRef,
          },
          clocks: initialClocks,
          serverOffset: 0,
          ply: 0,
          lastMove: null,
          pendingMove: null,
          premove: null,
          result: null,
          drawOfferBy: null,
          rejection: null,
          handicap: handicap ?? null,
          capturedPieces: (st.captured_pieces as CapturedPiece[]) ?? [],
          spectatorCount: 0,
          chatMessages: [],
          rematchOfferBy: null,
          rematchStartedNextMatchId: null,
        };

        viewRef.current = newView;
        setView(newView);

        // If player plays black or bot has handicap opening moves, bot goes first
        if (st.turn !== userSide) {
          scheduleBotMove(getSimulatedThinkingDelay(0));
        }
      });
      return () => {
        cancelled = true;
      };
    }

    const socket = new MatchSocket({
      matchId,
      onStatus: (conn) => {
        if (!cancelled) setView((v) => ({ ...v, conn }));
      },
      onFrame: (frame) => {
        if (!cancelled) setView((v) => reduce(v, frame, coreRef.current, socket));
      },
    });
    socketRef.current = socket;
    void getCore().then((c) => {
      if (cancelled) return;
      coreRef.current = c;
      setCore(c);
      socket.start(); // connect only once the engine can apply deltas
    });
    return () => {
      cancelled = true;
      socket.stop();
    };
  }, [matchId, isLocal]);

  useEffect(() => {
    if (!isLocal || view.status !== 'running' || view.result || !view.clocks?.deadline_at) return;
    const interval = setInterval(() => {
      const current = viewRef.current;
      if (current.status !== 'running' || current.result || !current.clocks?.deadline_at) return;
      if (Date.now() >= current.clocks.deadline_at) {
        const turn = current.clocks.turn;
        const winner: Side = turn === 'red' ? 'black' : 'red';
        const result = { winner, reason: 'TIMEOUT' as const };
        if (winner === current.yourSide) sfx.playVictory();
        else sfx.playDefeat();
        const updatedView: MatchView = {
          ...current,
          status: 'finished',
          result,
          clocks: current.clocks ? { ...current.clocks, deadline_at: null } : null,
        };
        viewRef.current = updatedView;
        setView(updatedView);
      }
    }, 500);
    return () => clearInterval(interval);
  }, [isLocal, view.status, view.result, view.clocks?.deadline_at]);

  const scheduleBotMove = (delay?: number) => {
    const liveView = viewRef.current;
    const finalDelay = delay ?? getSimulatedThinkingDelay(liveView.ply);
    setTimeout(async () => {
      const liveView = viewRef.current;
      const liveCore = coreRef.current;
      if (!liveCore || !liveView.state || liveView.status !== 'running' || liveView.result) return;
      if (liveView.state.turn === liveView.yourSide) return;
      try {
        const legal = liveCore.legalMoves(liveView.state);
        if (!legal || legal.moves.length === 0) return;

        let botMove: { from: number; to: number } | undefined = undefined;

        // Check if currently in handicap opening moves phase
        const isHandicapPhase =
          liveView.state.ruleset !== 'blind' &&
          liveView.handicap &&
          (liveView.state.ply ?? 0) < (liveView.handicap.moves_count ?? 0) &&
          liveView.state.turn === (liveView.handicap.giver_side === 'red' ? 'black' : 'red');

        if (isHandicapPhase) {
          // In handicap opening moves: legal.moves already strictly satisfies:
          // not crossing river, not capturing, not checking.
          const idx = Math.floor(Math.random() * legal.moves.length);
          botMove = legal.moves[idx];
        } else {
          // Thử hỏi Bot Engine trên Server trước (đáp ứng đúng bot đã active: Gen 85, 135k nodes, ...)
          // Đặt timeout 10s để bot đủ thời gian duyệt sâu 135,000 nodes
          try {
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), 12000);
            const serverRes = await api.getBotMove(
              {
                ruleset: liveView.state.ruleset,
                turn: liveView.state.turn as Side,
                publicState: liveView.state,
                history: localMovesRef.current,
              },
              controller.signal,
            );
            clearTimeout(timeoutId);
            if (
              serverRes?.move &&
              legal.moves.some((m) => m.from === serverRes.move.from && m.to === serverRes.move.to)
            ) {
              botMove = serverRes.move;
            }
          } catch {
            // Server timeout hoặc offline -> fallback ngay lập tức vào local WASM engine
          }

          if (!botMove) {
            try {
              const depth = 3;
              const searchRes = liveCore.search(liveView.state, { max_depth: depth });
              const candidate = searchRes?.move;
              if (candidate && legal.moves.some((m) => m.from === candidate.from && m.to === candidate.to)) {
                botMove = candidate;
              }
            } catch {
              // Search might fail on non-standard setups, fallback to legal moves
            }
          }

          if (!botMove) {
            const idx = Math.floor(Math.random() * legal.moves.length);
            botMove = legal.moves[idx];
          }
        }

        if (botMove) {
          const { state: botNextState, outcome: botOutcome } = liveCore.applyMove(liveView.state, botMove);
          if (botOutcome.captured) sfx.playCapture();
          else sfx.playMove();
          if (botOutcome.check) sfx.playCheck();

          if (botOutcome.result) {
            if (botOutcome.result.winner === liveView.yourSide) sfx.playVictory();
            else if (botOutcome.result.winner !== null) sfx.playDefeat();
          }

          localMovesRef.current.push({
            ply: liveView.ply,
            from: botMove.from,
            to: botMove.to,
            notation: botOutcome.notation ?? '',
            ms_spent: finalDelay,
          });

          const isOver = !!botOutcome.result;
          const status = isOver ? 'finished' : 'running';

          const nextClocks = computeNextClocks(
            liveView.clocks,
            liveView.state.turn,
            botNextState.turn,
            isOver,
          );

          persistLocalMatch(
            botNextState,
            status,
            botOutcome.result ?? null,
            liveView.state.ruleset,
            liveView.handicap,
            liveView.yourSide ?? 'red',
            'medium',
            nextClocks,
          );

          const updatedView: MatchView = {
            ...liveView,
            state: botNextState,
            ply: liveView.ply + 1,
            lastMove: botMove,
            pendingMove: null,
            capturedPieces: (botNextState.captured_pieces as CapturedPiece[]) ?? liveView.capturedPieces,
            result: botOutcome.result ?? null,
            status,
            clocks: nextClocks,
          };
          viewRef.current = updatedView;
          setView(updatedView);

          // If next turn is still bot (e.g. handicap opening moves)
          if (!botOutcome.result && botNextState.turn !== liveView.yourSide) {
            scheduleBotMove(getSimulatedThinkingDelay(liveView.ply + 1));
          }
        }
      } catch (e) {
        console.error('Local simulated player move error:', e);
      }
    }, finalDelay);
  };

  const actions = useMemo<MatchActions>(
    () => ({
      move(from, to) {
        const latest = viewRef.current;
        if (latest.state && latest.status === 'running' && !latest.result && latest.yourSide && latest.state.turn !== latest.yourSide) {
          sentPremoveRef.current = null;
          setView((v) => ({ ...v, premove: { from, to }, rejection: null }));
          return;
        }
        if (isLocal) {
          const currentCore = coreRef.current;
          const currentView = viewRef.current;
          if (!currentCore || !currentView.state || currentView.status !== 'running' || currentView.result) return;
          try {
            const { state: nextState, outcome } = currentCore.applyMove(currentView.state, { from, to });
            if (outcome.captured) sfx.playCapture();
            else sfx.playMove();
            if (outcome.check) sfx.playCheck();

            const isOver = !!outcome.result;
            if (outcome.result) {
              if (outcome.result.winner === currentView.yourSide) sfx.playVictory();
              else if (outcome.result.winner !== null) sfx.playDefeat();
            }

            localMovesRef.current.push({
              ply: currentView.ply,
              from,
              to,
              notation: outcome.notation ?? '',
              ms_spent: 200,
            });

            const nextClocks = computeNextClocks(
              currentView.clocks,
              currentView.state.turn,
              nextState.turn,
              isOver,
            );

            let botDiff = 'medium';
            try {
              const raw = localStorage.getItem('xq_saved_config');
              if (raw) botDiff = JSON.parse(raw).bot_difficulty || JSON.parse(raw).difficulty || 'medium';
            } catch {
              // ignore
            }

            persistLocalMatch(
              nextState,
              isOver ? 'finished' : 'running',
              outcome.result ?? null,
              currentView.state.ruleset,
              currentView.handicap,
              currentView.yourSide ?? 'red',
              botDiff,
              nextClocks,
            );

            sentPremoveRef.current = null;
            const updatedView: MatchView = {
              ...currentView,
              state: nextState,
              ply: currentView.ply + 1,
              lastMove: { from, to },
              pendingMove: null,
              premove: null,
              capturedPieces: (nextState.captured_pieces as CapturedPiece[]) ?? currentView.capturedPieces,
              result: outcome.result ?? null,
              status: isOver ? 'finished' : 'running',
              clocks: nextClocks,
            };
            viewRef.current = updatedView;
            setView(updatedView);

            // Simulated player response if not game over and it's opponent's turn
            if (!isOver && nextState.turn !== currentView.yourSide) {
              scheduleBotMove(getSimulatedThinkingDelay(currentView.ply + 1, outcome.check || !!outcome.captured));
            }
          } catch (e) {
            console.warn('Illegal move', e);
          }
          return;
        }

        sentPremoveRef.current = null;
        setView((v) => {
          if (!socketRef.current || v.status !== 'running' || v.result) return v;
          const commandId = crypto.randomUUID();
          socketRef.current.sendMove({ command_id: commandId, ply: v.ply, move: { from, to } });
          return { ...v, premove: null, pendingMove: { from, to }, rejection: null };
        });
      },
      cancelPremove() {
        sentPremoveRef.current = null;
        setView((v) => ({ ...v, premove: null }));
      },
      resign() {
        if (isLocal) {
          sfx.playDefeat();
          const latest = viewRef.current;
          const result: MatchResultBody = {
            winner: (latest.yourSide === 'red' ? 'black' : 'red') as Side,
            reason: 'RESIGN' as ResultReason,
          };
          if (latest.state) {
            let botDiff = 'medium';
            try {
              const raw = localStorage.getItem('xq_saved_config');
              if (raw) botDiff = JSON.parse(raw).bot_difficulty || JSON.parse(raw).difficulty || 'medium';
            } catch {
              // ignore
            }
            persistLocalMatch(
              latest.state,
              'finished',
              result,
              latest.state.ruleset,
              latest.handicap,
              latest.yourSide ?? 'red',
              botDiff,
            );
          }
          setView((v) => ({
            ...v,
            status: 'finished',
            result,
          }));
          return;
        }
        socketRef.current?.sendJson({ type: 'game.resign' });
      },
      offerDraw() {
        if (isLocal) {
          const latest = viewRef.current;
          const result: MatchResultBody = {
            winner: null,
            reason: 'DRAW_AGREED' as ResultReason,
          };
          if (latest.state) {
            let botDiff = 'medium';
            try {
              const raw = localStorage.getItem('xq_saved_config');
              if (raw) botDiff = JSON.parse(raw).bot_difficulty || JSON.parse(raw).difficulty || 'medium';
            } catch {
              // ignore
            }
            persistLocalMatch(
              latest.state,
              'finished',
              result,
              latest.state.ruleset,
              latest.handicap,
              latest.yourSide ?? 'red',
              botDiff,
            );
          }
          setView((v) => ({
            ...v,
            status: 'finished',
            result,
          }));
          return;
        }
        socketRef.current?.sendJson({ type: 'game.draw_offer' });
      },
      respondDraw(accept) {
        if (isLocal) {
          setView((v) => ({ ...v, drawOfferBy: null }));
          return;
        }
        socketRef.current?.sendJson({ type: 'game.draw_response', accept });
        setView((v) => ({ ...v, drawOfferBy: null }));
      },
      offerRematch() {
        if (isLocal) {
          const currentView = viewRef.current;
          if (!currentView.state || !coreRef.current) return;
          // Đổi bên đi tiên (Red <-> Black)
          const nextUserSide: Side = currentView.yourSide === 'red' ? 'black' : 'red';
          let nextHandicap = currentView.handicap;
          const ruleset = currentView.state.ruleset;
          if (nextHandicap && nextHandicap.type === 'asym_royal') {
            if (ruleset !== 'blind_si_chua' && ruleset !== 'blind_tuong_vuong') {
              nextHandicap = null;
            } else {
              const prevGiver = nextHandicap.giver_side ?? 'red';
              nextHandicap = { ...nextHandicap, giver_side: prevGiver === 'red' ? 'black' : 'red' };
            }
          }
          const engineHandicap = toEngineHandicap(ruleset, nextHandicap);
          localSeedRef.current = Date.now().toString();
          localMovesRef.current = [];
          localStartedAtRef.current = new Date().toISOString();
          const nextState = coreRef.current.newGame(ruleset, BigInt(localSeedRef.current), engineHandicap);
          const playerName = currentUser()?.display_name || 'Bạn';
          const sim = simPlayerRef.current || generateSimulatedPlayer();
          simPlayerRef.current = sim;
          const oppRef = simulatedToPlayerRef(sim);

          const now = Date.now();
          const rematchTurn = nextState.turn as Side;
          const rematchClocks: ClockState = {
            red_ms: 600_000,
            black_ms: 600_000,
            turn: rematchTurn,
            deadline_at: now + 600_000,
          };

          persistLocalMatch(
            nextState,
            'running',
            null,
            ruleset,
            nextHandicap ?? null,
            nextUserSide,
            'medium',
            rematchClocks,
          );

          const nextView: MatchView = {
            ...INITIAL,
            loaded: true,
            conn: 'live',
            status: 'running',
            state: nextState,
            yourSide: nextUserSide,
            sides: {
              red: nextUserSide === 'red' ? { user_id: 'player', display_name: playerName, connected: true } : oppRef,
              black: nextUserSide === 'black' ? { user_id: 'player', display_name: playerName, connected: true } : oppRef,
            },
            clocks: rematchClocks,
            serverOffset: 0,
            ply: 0,
            lastMove: null,
            pendingMove: null,
            premove: null,
            result: null,
            drawOfferBy: null,
            rematchOfferBy: null,
            rematchStartedNextMatchId: null,
            rejection: null,
            handicap: nextHandicap ?? null,
            capturedPieces: (nextState.captured_pieces as CapturedPiece[]) ?? [],
            spectatorCount: 0,
            chatMessages: [],
          };
          viewRef.current = nextView;
          setView(nextView);

          if (nextState.turn !== nextUserSide) {
            scheduleBotMove(getSimulatedThinkingDelay(0));
          }
          return;
        }
        socketRef.current?.sendJson({ type: 'game.rematch_offer' });
      },
      respondRematch(accept) {
        if (!isLocal) {
          socketRef.current?.sendJson({ type: 'game.rematch_response', accept });
        }
      },
      sendChat(text) {
        const trimmed = text.trim();
        if (!trimmed) return;
        if (isLocal) {
          setView((v) => ({
            ...v,
            chatMessages: [
              ...v.chatMessages,
              {
                match_id: matchId,
                sender_id: 'player',
                sender_name: 'Bạn',
                side: v.yourSide,
                text: trimmed,
                created_at: Date.now(),
              },
            ],
          }));
          return;
        }
        socketRef.current?.sendJson({ type: 'game.chat', text: trimmed });
      },
    }),
    [isLocal],
  );

  return { view, actions, core };
}

function reduce(v: MatchView, frame: Frame, core: XiangqiCore | null, socket: MatchSocket): MatchView {
  const offset = frame.server_time - Date.now();
  switch (frame.type) {
    case 'match.snapshot': {
      const p = frame.payload as MatchSnapshotPayload;
      const finalState = p.final_state as GameStateJson | undefined;
      return {
        ...v,
        loaded: true,
        status: p.status,
        state: finalState ? revealFinishedState(finalState) : (p.state as GameStateJson),
        yourSide: p.your_side,
        sides: p.sides,
        clocks: p.clocks,
        serverOffset: offset,
        ply: p.last_ply,
        pendingMove: null,
        premove: null,
        result: p.result,
        drawOfferBy: p.draw_offer_by,
        handicap: p.handicap ?? null,
        capturedPieces:
          (finalState ? revealFinishedState(finalState).captured_pieces as CapturedPiece[] : undefined) ??
          ((p.state as GameStateJson)?.captured_pieces as CapturedPiece[] | undefined) ??
          [],
        spectatorCount: p.spectators_count ?? v.spectatorCount,
      };
    }
    case 'match.started': {
      const p = frame.payload as MatchStartedPayload;
      return { ...v, status: 'running', clocks: p.clocks, serverOffset: offset };
    }
    case 'match.move': {
      const p = frame.payload as MatchMovePayload;
      socket.ackMove(p.ply);
      let state = v.state;
      if (core && state) {
        try {
          state = core.applyDelta(
            state,
            p.move,
            p.revealed?.kind as PieceKindId | undefined,
            p.captured
              ? {
                  id: p.captured.square,
                  square: p.captured.square,
                  kind: p.captured.kind as PieceKindId | null,
                  side: p.captured.side,
                  was_covered: p.captured.was_covered ?? false,
                  captured_by: p.captured.captured_by ?? p.by,
                }
              : undefined,
            p.reveals as Array<{ square: number; kind: PieceKindId }> | undefined,
          );
        } catch {
          state = v.state; // snapshot will resync on next reconnect
        }
      }
      let capturedPieces = v.capturedPieces;
      if (p.captured) {
        capturedPieces = [
          ...capturedPieces,
          {
            id: p.captured.square,
            kind: p.captured.kind,
            side: p.captured.side,
            was_covered: p.captured.was_covered ?? false,
            captured_by: p.captured.captured_by ?? p.by,
          },
        ];
      }
      if (p.captured) {
        sfx.playCapture();
      } else {
        sfx.playMove();
      }
      if (p.check) {
        sfx.playCheck();
      }
      if (p.result) {
        if (v.yourSide && p.result.winner === v.yourSide) sfx.playVictory();
        else if (v.yourSide && p.result.winner !== null) sfx.playDefeat();
      }

      return {
        ...v,
        state,
        ply: p.ply,
        clocks: p.clocks,
        serverOffset: offset,
        lastMove: p.move,
        pendingMove: null,
        drawOfferBy: null,
        result: p.result ?? v.result,
        status: p.result ? 'finished' : v.status,
        capturedPieces: (state?.captured_pieces as CapturedPiece[] | undefined) ?? capturedPieces,
      };
    }
    case 'match.move_rejected': {
      const p = frame.payload as { code: MoveRejectCode };
      socket.clearPending();
      return {
        ...v,
        pendingMove: null,
        premove: null,
        rejection: { text: REJECT_TEXT[p.code] ?? p.code, at: Date.now() },
      };
    }
    case 'match.ended': {
      const p = frame.payload as MatchEndedPayload;
      const rawFinalState = p.final_state as GameStateJson | undefined;
      const finalState = rawFinalState ? revealFinishedState(rawFinalState) : undefined;
      if (v.yourSide && p.result.winner === v.yourSide) sfx.playVictory();
      else if (v.yourSide && p.result.winner !== null) sfx.playDefeat();
      return {
        ...v,
        status: 'finished',
        state: finalState ?? v.state,
        result: p.result,
        clocks: p.clocks,
        serverOffset: offset,
        pendingMove: null,
        premove: null,
        capturedPieces: (finalState?.captured_pieces as CapturedPiece[] | undefined) ?? v.capturedPieces,
      };
    }
    case 'match.chat': {
      const p = frame.payload as MatchChatPayload;
      return { ...v, chatMessages: [...v.chatMessages, p] };
    }
    case 'match.spectators': {
      const p = frame.payload as MatchSpectatorsPayload;
      return { ...v, spectatorCount: p.count };
    }
    case 'match.presence': {
      const p = frame.payload as { side: Side; connected: boolean };
      if (!v.sides) return v;
      return {
        ...v,
        sides: {
          ...v.sides,
          [p.side]: { ...v.sides[p.side], connected: p.connected },
        },
      };
    }
    case 'match.draw_offer': {
      const p = frame.payload as { by: Side };
      return { ...v, drawOfferBy: p.by };
    }
    case 'match.rematch_offered': {
      const p = frame.payload as { match_id: string; by: Side };
      return { ...v, rematchOfferBy: p.by };
    }
    case 'match.rematch_started': {
      const p = frame.payload as { match_id: string; next_match_id: string };
      return { ...v, rematchStartedNextMatchId: p.next_match_id };
    }
    case 'match.rematch_declined': {
      return { ...v, rematchOfferBy: null };
    }
    default:
      return v;
  }
}

/** Finished blind games reveal every surviving covered piece in the UI. */
function revealFinishedState(state: GameStateJson): GameStateJson {
  return {
    ...state,
    board: {
      ...state.board,
      squares: state.board.squares.map((piece) =>
        piece?.covered ? { ...piece, covered: false, revealed_at_end: true } : piece,
      ),
    },
  };
}
